import React, { useRef, useEffect, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';

import { useOpen } from 'hooks/use-open';
import { useClickOutside } from 'hooks/use-click-outside';

import Button from 'components/partials/button/button';
import type { ButtonSize } from 'components/partials/button/button';
import Checkbox from 'components/partials/checkbox/checkbox';
import RadioInput from 'components/partials/radio-input/radio-input';
import IconButton from 'components/partials/button/icon-button';
import { useModalFocusTrap } from 'components/partials/modal/use-modal-focus-trap';

import CollapsibleFiltersSection from './collapsible-filter-section';

import { hasAnyFundsFilter } from '../helpers/funds-filters.helpers';

import {
  FUND_SOURCE_OPTIONS,
  FUND_TYPE_OPTIONS,
  BOOL_FILTER_OPTIONS,
} from '../funds-step.constants';
import { EMPTY_FUNDS_FILTERS } from '../funds-step.types';
import type { FundsFilterValues } from '../funds-step.types';

import Icons from 'assets/icons';

const PANEL_WIDTH_PX = 318;

interface FundsFiltersProps {
  values: FundsFilterValues;
  onApply: (filters: FundsFilterValues) => void;
  buttonSize?: ButtonSize;
}

const FundsFilters = ({
  values,
  onApply,
  buttonSize = 'md',
}: FundsFiltersProps): React.JSX.Element => {
  const { isOpen, handleClose, handleToggle } = useOpen();
  const [draft, setDraft] = useState<FundsFilterValues>(values);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const buttonRef = useRef<HTMLDivElement>(null);
  const triggerButtonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const wasOpenRef = useRef(false);

  // Reuses the Modal focus trap (Tab cycling) for this portaled popover panel
  // (WCAG 2.4.3). Enabled only while open: the panel portals outside the parent
  // modal's DOM, so an always-on trap would let the parent modal (topmost on the
  // shared stack) pull Tab focus back out of the panel. Gating on `isOpen` pushes
  // this panel onto the stack as topmost only when it is actually shown.
  useModalFocusTrap(panelRef, isOpen);

  useEffect(() => {
    if (isOpen) {
      panelRef.current?.focus();
    } else if (wasOpenRef.current) {
      // Only restore focus to the trigger on an actual open→close
      // transition (WCAG 3.2.1) — not on initial mount, since this panel
      // isn't rendered inside the shared Modal and nothing else claims
      // focus first.
      triggerButtonRef.current?.focus();
    }

    wasOpenRef.current = isOpen;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscape = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;

      setDraft(values);
      handleClose();
    };

    // Native listener (not a JSX handler) so this stays off the `role="dialog"`
    // panel element for jsx-a11y/no-noninteractive-element-interactions,
    // matching the Modal component's own document-level keydown pattern.
    document.addEventListener('keydown', handleEscape);

    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, values, handleClose]);

  useEffect(() => {
    const panel = panelRef.current;

    if (!isOpen || !panel) return undefined;

    const stopPropagation = (event: MouseEvent): void => {
      event.stopPropagation();
    };

    panel.addEventListener('mousedown', stopPropagation);

    return () => panel.removeEventListener('mousedown', stopPropagation);
  }, [isOpen]);

  useEffect(() => {
    setDraft(values);
  }, [values]);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();

      setPosition({
        top: rect.bottom + 4,
        left: Math.max(8, rect.right - PANEL_WIDTH_PX),
      });
    }
  }, [isOpen]);

  const handleClickOutside = useCallback(() => {
    setDraft(values);
    handleClose();
  }, [values, handleClose]);

  useClickOutside(buttonRef, handleClickOutside, isOpen);

  const toggleCheckbox = useCallback(
    (field: 'source' | 'type', value: string): void => {
      setDraft((prev) => {
        const current = prev[field];
        const next = current.includes(value)
          ? current.filter((v) => v !== value)
          : [...current, value];

        return { ...prev, [field]: next };
      });
    },
    []
  );

  const setRadio = useCallback(
    (field: 'instAid' | 'totalAid', value: boolean): void => {
      setDraft((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const handleApply = (): void => {
    onApply(draft);
    handleClose();
  };

  const handleClear = (): void => {
    setDraft(EMPTY_FUNDS_FILTERS);
    onApply(EMPTY_FUNDS_FILTERS);
    handleClose();
  };

  const isDraftDirty = hasAnyFundsFilter(draft);

  const dropdownPanel = isOpen
    ? createPortal(
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Filters"
          aria-modal="true"
          tabIndex={-1}
          className="fixed bg-white rounded-lg z-[800] overflow-hidden border border-navy-100 shadow-[0px_2px_8px_-2px_rgba(0,45,93,0.2)]"
          style={{
            top: position.top,
            left: position.left,
            width: PANEL_WIDTH_PX,
          }}
          data-testid="filters-dropdown"
        >
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
            <span className="font-bold text-base text-gray-700">Filters</span>
            <IconButton
              variant="reserve-text"
              size="xs"
              icon={<Icons.Close />}
              onClick={() => {
                setDraft(values);
                handleClose();
              }}
              aria-label="Close filters"
              data-testid="close-filters"
            />
          </div>

          <div className="flex flex-col px-5 pb-1 pt-4 max-h-[376px] overflow-y-auto">
            <CollapsibleFiltersSection
              title="Source"
              defaultOpen={!!draft.source.length}
            >
              {FUND_SOURCE_OPTIONS.map(({ value, label }) => (
                <Checkbox
                  key={value}
                  labelFor={`filter-source-${value}`}
                  checkboxValue={value}
                  label={label}
                  checked={draft.source.includes(value)}
                  onChange={() => toggleCheckbox('source', value)}
                  data-testid={`filter-checkbox-source-${value}`}
                />
              ))}
            </CollapsibleFiltersSection>

            <CollapsibleFiltersSection
              title="Type"
              defaultOpen={!!draft.type.length}
            >
              {FUND_TYPE_OPTIONS.map(({ value, label }) => (
                <Checkbox
                  key={value}
                  labelFor={`filter-type-${value}`}
                  checkboxValue={value}
                  label={label}
                  checked={draft.type.includes(value)}
                  onChange={() => toggleCheckbox('type', value)}
                  data-testid={`filter-checkbox-type-${value}`}
                />
              ))}
            </CollapsibleFiltersSection>

            <CollapsibleFiltersSection
              title="Institutional aid"
              defaultOpen={draft.instAid !== null}
            >
              <div className="flex gap-4">
                {BOOL_FILTER_OPTIONS.map(({ value, label }) => (
                  <RadioInput
                    key={value}
                    labelFor={`filter-instAid-${value}`}
                    radioValue={value}
                    label={label}
                    value={draft.instAid === null ? '' : String(draft.instAid)}
                    onChange={() => setRadio('instAid', value === 'true')}
                    customClass="!mb-0"
                    data-testid={`filter-radio-instAid-${value}`}
                  />
                ))}
              </div>
            </CollapsibleFiltersSection>

            <CollapsibleFiltersSection
              title="Total aid"
              defaultOpen={draft.totalAid !== null}
            >
              <div className="flex gap-4">
                {BOOL_FILTER_OPTIONS.map(({ value, label }) => (
                  <RadioInput
                    key={value}
                    labelFor={`filter-totalAid-${value}`}
                    radioValue={value}
                    label={label}
                    value={
                      draft.totalAid === null ? '' : String(draft.totalAid)
                    }
                    onChange={() => setRadio('totalAid', value === 'true')}
                    customClass="!mb-0"
                    data-testid={`filter-radio-totalAid-${value}`}
                  />
                ))}
              </div>
            </CollapsibleFiltersSection>
          </div>

          <div className="flex items-center gap-4 px-5 pt-2 pb-4">
            <Button
              type="button"
              variant="brand-filled"
              size="sm"
              onClick={handleApply}
              disabled={!isDraftDirty}
              data-testid="apply-filters"
            >
              Apply
            </Button>
            <Button
              type="button"
              variant="reserve-text"
              size="sm"
              onClick={handleClear}
              disabled={!isDraftDirty}
              data-testid="clear-filters"
            >
              Clear
            </Button>
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div className="relative flex-shrink-0">
      <div ref={buttonRef}>
        <Button
          ref={triggerButtonRef}
          type="button"
          variant="reserve-outlined"
          size={buttonSize}
          onClick={handleToggle}
          iconBefore={<Icons.FilterIcon />}
          data-testid="filters-button"
        >
          Filters
        </Button>
      </div>

      {dropdownPanel}
    </div>
  );
};

export default FundsFilters;
